/**
 * The fork's one cookie reader.
 *
 * `develop` read cookies in two shapes (design defect 8):
 *   PACSApiClient.ts:95-101 (pacsUrl) and domainManagement.service.ts's
 *   getAccessToken:15-21 (accessToken JWT — truncates, and was live on every
 *   viewer-backend REST call plus the uploader), getViewerUrl:27-31
 *   (viewerUrl), getPacsUrl:33-39 (pacsUrl, dead) —
 *     split('; '), no trim, `split('=')[1]` — truncates at the first '=',
 *     which breaks any base64-padded value. All four decode.
 *   CustomAuthenticationService.ts:88-97 — the accessToken JWT's OTHER
 *   reader, and the correct one —
 *     split(';'), trim, `substring(key.length + 1)` — already first-'='-only,
 *     but never runs the value through `decodeURIComponent`.
 *
 * Returns `null` when the cookie is ABSENT and `''` when it is PRESENT BUT
 * EMPTY. Callers depend on the difference: §5.4 point 3 requires an empty
 * `pacsUrl` to fail loudly rather than fall back to the config's relative roots,
 * which silently work on the local proxy and silently break in deployment.
 */
export function readCookie(name: string): string | null {
  const prefix = `${name}=`;

  for (const entry of document.cookie.split(';')) {
    const cookie = entry.trim();

    if (!cookie.startsWith(prefix)) {
      continue;
    }

    // Everything after the FIRST '=' — not split('=')[1].
    const value = cookie.slice(prefix.length);

    try {
      return decodeURIComponent(value);
    } catch {
      // A malformed escape ('%zz') throws URIError. readCookie runs inside
      // preRegistration, where ExtensionManager.ts:239-241 swallows throws into
      // a console.error and boots the app half-registered. Returning the raw
      // value is what two of develop's three readers already did.
      return value;
    }
  }

  return null;
}
